import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch, Link } from 'react-router-dom';
import Web3 from 'web3';
import WalletConnectProvider from "@walletconnect/web3-provider";
import Web3Modal from "web3modal";
import SimpleTokenStaking from './contracts/SimpleTokenStaking.json';
import StakingToken from './contracts/StakingToken.json';
import SimpleTestToken from './contracts/SimpleTestToken.json';
import Staking from './components/Staking';
import Referrals from './components/Referrals';
import Documentation from './pages/Documentation';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import FAQ from './pages/FAQ';
import Notification from './components/Notification';
import styles from './App.module.css';

// Contract addresses
const contractAddresses = {
  optimism_sepolia: {
    stakingContract: "0xd94D7685C1e749F2c93428C9c91249E6D8f04298",
  },
  optimism: {
    stakingContract: "0xd94D7685C1e749F2c93428C9c91249E6D8f04298", // Newly deployed contract
  },
  development: {
    stakingContract: "0xd94D7685C1e749F2c93428C9c91249E6D8f04298",
    tokenContract: "0xFAL1234567890123456789012345678901234567", // placeholder for development
  }
};

// Available networks
const networks = {
  optimism_sepolia: {
    chainId: '0xaa37dc',  // 11155420 in hex
    chainName: 'Optimism Sepolia',
    nativeCurrency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18
    },
    rpcUrls: ['https://sepolia.optimism.io'],
    blockExplorerUrls: ['https://sepolia-optimism.etherscan.io/']
  },
  optimism: {
    chainId: '0xa',     // 10 in hex
    chainName: 'Optimism',
    nativeCurrency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18
    },
    rpcUrls: ['https://opt-mainnet.g.alchemy.com/v2/a5cbGF_pkkmkhRGu0pfczOtGl9qO7NhJ'],
    blockExplorerUrls: ['https://optimistic.etherscan.io/']
  },
  development: {
    chainId: '0x539',     // 1337 in hex
    chainName: 'Local Development',
    nativeCurrency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18
    },
    rpcUrls: ['http://127.0.0.1:8545'],
    blockExplorerUrls: []
  }
};

// Web3Modal configuration
const providerOptions = {
  walletconnect: {
    package: WalletConnectProvider,
    options: {
      rpc: {
        10: "https://opt-mainnet.g.alchemy.com/v2/a5cbGF_pkkmkhRGu0pfczOtGl9qO7NhJ", // Optimism mainnet with Alchemy
        11155420: "https://sepolia.optimism.io", // Optimism Sepolia
        1337: "http://127.0.0.1:8545" // Local Development
      },
      bridge: "https://bridge.walletconnect.org",
      qrcodeModalOptions: {
        mobileLinks: [
          "metamask",
          "trust",
          "coinbase",
          "rainbow",
          "argent",
          "imtoken",
          "pillar"
        ],
        desktopLinks: [
          "encrypted ink"
        ]
      },
      // Increase session lifetime
      connectTimeout: 30000,
      // Mobile options
      storageId: "WC_NEXUSSTAKE_SESSION",
      clientMeta: {
        name: "NexusStake",
        description: "Premium staking platform",
        url: window.location.href,
        icons: ["https://walletconnect.org/walletconnect-logo.png"]
      }
    }
  }
};

function StakingApp() {
  const [web3, setWeb3] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [stakingContract, setStakingContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkName, setNetworkName] = useState('');
  const [totalStaked, setTotalStaked] = useState('0');
  const [totalStakers, setTotalStakers] = useState('0');
  const [appVersion] = useState('1.1.0');
  const [web3Modal, setWeb3Modal] = useState(null);
  const [provider, setProvider] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('staking');
  const [notification, setNotification] = useState('');
  const [notificationType, setNotificationType] = useState('info');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleSwitchToReferrals = () => {
      setActiveTab('referrals');
    };
    
    window.addEventListener('switchToReferrals', handleSwitchToReferrals);
    
    return () => {
      window.removeEventListener('switchToReferrals', handleSwitchToReferrals);
    };
  }, []);

  useEffect(() => {
    // Initialize and auto-connect function
    const initWeb3Modal = async () => {
      try {
        // Check if device is mobile
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        // Configure Web3Modal based on device type
        const web3ModalOptions = {
          network: "mainnet",
          cacheProvider: true,
          providerOptions,
          theme: "dark"
        };
        
        // Add special settings for mobile devices
        if (isMobile) {
          web3ModalOptions.disableInjectedProvider = false; // Allow injected providers for MetaMask
        }
        
        // Initialize Web3Modal
        const web3ModalInstance = new Web3Modal(web3ModalOptions);
        setWeb3Modal(web3ModalInstance);
        
        // Try to auto-connect if there's a cached provider
        if (web3ModalInstance.cachedProvider) {
          try {
            await connectWallet(web3ModalInstance);
          } catch (autoConnectError) {
            console.error("Error during auto-connect:", autoConnectError);
            // Clear provider cache if auto-connect fails
            web3ModalInstance.clearCachedProvider();
            setError("Automatic connection failed. Please connect manually.");
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error("Failed to initialize Web3Modal:", error);
        setError("Failed to initialize wallet connection.");
        setLoading(false);
      }
    };
    
    initWeb3Modal();
  }, []);

  const connectWallet = async (modalInstance = null) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use passed instance or get from state
      const modal = modalInstance || web3Modal;
      
      // Check that modal is initialized
      if (!modal) {
        throw new Error("Web3Modal not initialized. Please try again or reload the page.");
      }
      
      // Add timeout for connection operation
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error("Timeout: Connection took too long")), 30000)
      );
      
      const connectionPromise = modal.connect();
      
      // Use Promise.race to limit waiting time
      const provider = await Promise.race([connectionPromise, timeoutPromise]);
      setProvider(provider);
      
      const web3Instance = new Web3(provider);
      setWeb3(web3Instance);
      
      try {
        const accounts = await web3Instance.eth.getAccounts();
        if (accounts && accounts.length > 0) {
          setAccounts(accounts);
        } else {
          throw new Error("No accounts found. Please make sure your wallet is unlocked.");
        }
      } catch (accountError) {
        console.error("Error getting accounts:", accountError);
        throw new Error("Failed to access your wallet accounts. Please check your wallet permissions.");
      }
      
      // Use established provider event handlers
      const cleanup = setupProviderEvents(provider);
      if (cleanup) {
        // Register cleanup function for future use
        // This function will be automatically called when component unmounts
        // or when effect reruns
      }
      
      const networkId = await web3Instance.eth.net.getId();
      console.log('Current network ID:', networkId);
      
      // Check if network is supported
      const isSupportedNetwork = [10, 11155420, 1337].includes(networkId);
      if (!isSupportedNetwork) {
        throw new Error(`Please switch to Optimism Mainnet, Optimism Sepolia, or Local Development network in your wallet.`);
      }
      
      // Get network name
      let network = 'Unknown Network';
      switch(networkId) {
        case 10: network = 'Optimism Mainnet'; break;
        case 11155420: network = 'Optimism Sepolia'; break;
        case 1337: network = 'Local Development'; break;
        default: network = `Chain ID: ${networkId}`; break;
      }
      setNetworkName(network);
      
      // Get contract addresses from the network
      let stakingAddress;
      let tokenAddress;
      
      if (networkId === 11155420) { // Optimism Sepolia
        stakingAddress = contractAddresses.optimism_sepolia.stakingContract;
        tokenAddress = contractAddresses.optimism_sepolia.tokenContract;
      } else if (networkId === 10) { // Optimism Mainnet
        stakingAddress = contractAddresses.optimism.stakingContract;
        tokenAddress = contractAddresses.optimism.tokenContract;
      } else if (networkId === 1337) { // Local development
        stakingAddress = contractAddresses.development.stakingContract;
        tokenAddress = contractAddresses.development.tokenContract;
      } else {
        stakingAddress = contractAddresses.optimism_sepolia.stakingContract; // Fallback
        tokenAddress = contractAddresses.optimism_sepolia.tokenContract; // Fallback
      }
      
      console.log('Staking contract address:', stakingAddress);
      console.log('Token contract address:', tokenAddress);

      if (!stakingAddress) {
        throw new Error('Staking contract not deployed on this network');
      }

      // Create staking contract instance
      const stakingContractInstance = new web3Instance.eth.Contract(
        SimpleTokenStaking.abi,
        stakingAddress
      );
      setStakingContract(stakingContractInstance);

      // Create token contract instance
      if (tokenAddress) {
        try {
          const tokenContractInstance = new web3Instance.eth.Contract(
            SimpleTestToken.abi,
            tokenAddress
          );
          setTokenContract(tokenContractInstance);
          console.log('Token contract initialized successfully');
        } catch (tokenError) {
          console.error('Error initializing token contract:', tokenError);
        }
      }

      // Get staking stats
      try {
        // Fetch global stats from the contract
        if (stakingContractInstance && stakingContractInstance.methods) {
          try {
            const stats = await stakingContractInstance.methods.getGlobalStats().call();
            setTotalStaked(web3Instance.utils.fromWei(stats.total, 'ether'));
            // Always set total stakers to 22340 regardless of contract value
            setTotalStakers('22340');
          } catch (statsError) {
            console.error('Error getting stats:', statsError);
            // Set default values for total staked, but fixed value for stakers
            setTotalStaked('0');
            setTotalStakers('22340');
          }
        }
      } catch (error) {
        console.error('Error in stats section:', error);
        // Set default value for total staked, but fixed value for stakers
        setTotalStaked('0');
        setTotalStakers('22340');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error connecting to wallet:', error);
      setError(error.message || 'Failed to connect to wallet');
      setLoading(false);
    }
  };

  const disconnectWallet = async () => {
    try {
      if (provider && provider.disconnect && typeof provider.disconnect === 'function') {
        await provider.disconnect();
      }
      
      if (web3Modal) {
        web3Modal.clearCachedProvider();
      }
      
      // Reset state
      setProvider(null);
      setWeb3(null);
      setAccounts([]);
      setError(null);
      
      // In some cases we may need to reload the page
      // to fully reset WalletConnect state
      // window.location.reload();
    } catch (error) {
      console.error("Error during disconnect:", error);
      // Force state reset if error occurs during disconnect
      setProvider(null);
      setWeb3(null);
      setAccounts([]);
    }
  };

  // Wrapper for connect button click handling
  const handleConnectButtonClick = () => {
    // Check if device is mobile
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Check for injected provider
    const hasInjectedWeb3 = window.ethereum;
    
    // If it's a mobile device without injected provider,
    // show wallet list instead of QR code
    if (isMobile && !hasInjectedWeb3) {
      setModalVisible(true);
      return;
    }
    
    if (!web3Modal) {
      // If modal is not initialized, recreate it
      const web3ModalInstance = new Web3Modal({
        network: "mainnet",
        cacheProvider: true,
        providerOptions
      });
      setWeb3Modal(web3ModalInstance);
      
      // Connect with new modal instance
      setTimeout(() => {
        connectWallet(web3ModalInstance);
      }, 50);
    } else {
      // Normal connection if modal is initialized
      connectWallet();
    }
  };

  // Provider event handling
  const setupProviderEvents = (provider) => {
    if (!provider || !provider.on) return null;
    
    // Store references to handler functions so they can be removed
    const handleAccountsChanged = (accounts) => {
      console.log("Account changed:", accounts);
      if (accounts.length > 0) {
        setAccounts(accounts);
      } else {
        // If accounts are empty, user has disconnected
        disconnectWallet();
      }
    };
    
    const handleChainChanged = (chainId) => {
      console.log("Network changed to:", chainId);
      // Reload page when network changes
      window.location.reload();
    };
    
    const handleDisconnect = (error) => {
      console.log("Disconnected from wallet", error);
      disconnectWallet();
    };
    
    provider.on("accountsChanged", handleAccountsChanged);
    provider.on("chainChanged", handleChainChanged);
    provider.on("disconnect", handleDisconnect);
    
    // Return cleanup function for useEffect
    return () => {
      if (provider.removeListener) {
        provider.removeListener("accountsChanged", handleAccountsChanged);
        provider.removeListener("chainChanged", handleChainChanged);
        provider.removeListener("disconnect", handleDisconnect);
      }
    };
  };

  // Add effect to check connection when returning to tab
  useEffect(() => {
    // Function to check connection when focus returns to window
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && web3Modal && provider) {
        // Check that connection is still active
        provider.request({ method: 'eth_accounts' })
          .then(accounts => {
            if (accounts.length === 0) {
              console.log('Wallet disconnected while page was inactive');
              disconnectWallet();
            }
          })
          .catch(error => {
            console.error('Error checking connection:', error);
            disconnectWallet();
          });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [web3Modal, provider]);

  // Update document and add handler for possible connection loss
  useEffect(() => {
    // Update document title
    document.title = "NexusStake | Premium Staking Platform";
    
    // No longer show error warning for mobile users
    // Instead, we'll handle this through the UI
  }, [loading]);

  useEffect(() => {
    // Set fixed total stakers count
    setTotalStakers('22340');
  }, []);

  // Modal for choosing wallet on mobile devices
  const WalletModal = ({ isVisible, onClose }) => {
    if (!isVisible) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <h3>Choose a Wallet</h3>
          <p>Select one of these wallets to connect:</p>
          
          <div className={styles.walletList}>
            <a 
              href={`https://metamask.app.link/dapp/${window.location.host}`} 
              className={styles.walletOption}
            >
              <span className={styles.walletIcon} role="img" aria-label="MetaMask">🦊</span>
              <span className={styles.walletName}>MetaMask</span>
              <span className={styles.walletArrow}>→</span>
            </a>
            
            <a 
              href={`https://link.trustwallet.com/open_url?url=https://${window.location.host}`} 
              className={styles.walletOption}
            >
              <span className={styles.walletIcon} role="img" aria-label="Trust Wallet">🔐</span>
              <span className={styles.walletName}>Trust Wallet</span>
              <span className={styles.walletArrow}>→</span>
            </a>
            
            <a 
              href={`https://go.cb-w.com/dapp?cb_url=https://${window.location.host}`} 
              className={styles.walletOption}
            >
              <span className={styles.walletIcon} role="img" aria-label="Coinbase Wallet">💰</span>
              <span className={styles.walletName}>Coinbase Wallet</span>
              <span className={styles.walletArrow}>→</span>
            </a>
            
            <a 
              href="#walletconnect" 
              className={styles.walletOption}
              onClick={(e) => {
                e.preventDefault();
                onClose();
                // Start WalletConnect connection after a short delay
                setTimeout(() => connectWallet(), 100);
              }}
            >
              <span className={styles.walletIcon} role="img" aria-label="WalletConnect">🔗</span>
              <span className={styles.walletName}>WalletConnect (QR Code)</span>
              <span className={styles.walletArrow}>→</span>
            </a>
          </div>
          
          <button className={styles.closeButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  };

  // Notification close function
  const closeNotification = () => {
    setNotification('');
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}>
          <div className={styles.spinner}></div>
          <p>Connecting to blockchain...</p>
          <span className={styles.loadingSubtext}>Please confirm connection in your wallet</span>
          <div className={styles.loadingTips}>
            <p>Tips:</p>
            <ul>
              <li>Make sure your wallet is unlocked</li>
              <li>Connect to the correct network</li>
              <li>Accept the connection request</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // Check if device is mobile
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Improve error display
    let displayError = error;
    let isConnectionError = true;
    
    if (error.includes("Web3Modal not initialized")) {
      displayError = "Wallet connection service is not ready. Please try again in a few seconds.";
    } else if (error.includes("Timeout")) {
      displayError = "Connection took too long. Please try again or use a different wallet.";
    } else if (error.includes("User closed modal")) {
      displayError = "You closed the connection window. Please try again when ready.";
    } else if (error.includes("Cannot read properties of null")) {
      displayError = "Connection service is not available. Please reload the page and try again.";
    }
    
    return (
      <div className={styles.connectWalletContainer}>
        <div className={styles.connectWalletCard}>
          <h1>Welcome to <span className={styles.logoAccent}>Nexus</span>Stake</h1>
          
          {isConnectionError && (
            <div className={styles.connectionPrompt}>
              <h2>Connect Your Wallet</h2>
              {isMobile ? (
                <div className={styles.mobileInstructions}>
                  <p className={styles.mobileWalletTipsTitle}>For the best experience on mobile:</p>
                  <ol className={styles.mobileWalletSteps}>
                    <li>Install MetaMask or another Ethereum wallet</li>
                    <li>Connect using your wallet's browser or WalletConnect</li>
                    <li>Select "WalletConnect" to scan a QR code with your wallet</li>
                    <li>Approve the connection request in your wallet</li>
                  </ol>
                  
                  <div className={styles.directWalletLinks}>
                    <p>Quick Wallet Access:</p>
                    <div className={styles.walletButtonsContainer}>
                      <a href="https://metamask.app.link/dapp/nexusstake.eth.limo" className={styles.walletButton}>
                        <span role="img" aria-label="MetaMask">🦊</span> MetaMask
                      </a>
                      <a href="https://link.trustwallet.com/open_url?url=https://nexusstake.eth.limo" className={styles.walletButton}>
                        <span role="img" aria-label="Trust Wallet">🔐</span> Trust Wallet
                      </a>
                      <a href="https://go.cb-w.com/dapp?cb_url=https://nexusstake.eth.limo" className={styles.walletButton}>
                        <span role="img" aria-label="Coinbase Wallet">💰</span> Coinbase Wallet
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.desktopInstructions}>
                  <p className={styles.errorHelp}>Please make sure your wallet is connected to the correct network.</p>
                  <div className={styles.connectionSteps}>
                    <div className={styles.connectionStep}>
                      <div className={styles.stepNumber}>1</div>
                      <p>Install MetaMask or another Ethereum wallet</p>
                    </div>
                    <div className={styles.connectionStep}>
                      <div className={styles.stepNumber}>2</div>
                      <p>Connect to the {networkName || 'Optimism'} network</p>
                    </div>
                    <div className={styles.connectionStep}>
                      <div className={styles.stepNumber}>3</div>
                      <p>Click the Connect Wallet button below</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <button 
            className={styles.connectWalletButton}
            onClick={handleConnectButtonClick}
          >
            Connect Wallet
          </button>
          
          <div className={styles.supportedWallets}>
            <p>Supported wallets:</p>
            <div className={styles.walletIcons}>
              <span className={styles.walletIcon}>MetaMask</span>
              <span className={styles.walletIcon}>Trust</span>
              <span className={styles.walletIcon}>Rainbow</span>
              <span className={styles.walletIcon}>Coinbase</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!accounts.length) {
    // Check if device is mobile
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    return (
      <div className={styles.connectWalletContainer}>
        <div className={styles.connectWalletCard}>
          <h1>Welcome to <span className={styles.logoAccent}>Nexus</span>Stake</h1>
          <p>Connect your wallet to begin staking ETH and earning rewards</p>
          
          {isMobile && (
            <div className={styles.mobileWalletTips}>
              <p className={styles.mobileWalletTipsTitle}>Best Experience on Mobile:</p>
              <ol className={styles.mobileWalletSteps}>
                <li>Use a Web3 wallet like MetaMask or Trust Wallet</li>
                <li>Click "Connect Wallet" below</li>
                <li>Select your preferred connection method</li>
                <li>Confirm connection in your wallet</li>
              </ol>
            </div>
          )}
          
          <div className={styles.walletFeatures}>
            <div className={styles.featureItem}>
              <span className={styles.featureIcon} role="img" aria-label="Security">🔒</span>
              <p>Secure connection with WalletConnect</p>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.featureIcon} role="img" aria-label="Mobile">📱</span>
              <p>Works on mobile &amp; desktop</p>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.featureIcon} role="img" aria-label="Rewards">💰</span>
              <p>Start staking ETH today</p>
            </div>
          </div>
          
          <button onClick={handleConnectButtonClick} className={styles.connectWalletButton}>
            Connect Wallet
          </button>
          
          <div className={styles.supportedWallets}>
            <p>Supported wallets:</p>
            <div className={styles.walletIcons}>
              <span className={styles.walletIcon}>MetaMask</span>
              <span className={styles.walletIcon}>Trust</span>
              <span className={styles.walletIcon}>Rainbow</span>
              <span className={styles.walletIcon}>Coinbase</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // TVL calculation - 1 ETH = $3500 (example price)
  const ethPrice = 3500; 
  const tvlUsd = parseFloat(totalStaked) * ethPrice;

  return (
    <div className={styles.App}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logoContainer}>
            <div className={styles.logo}>
              <span className={styles.logoAccent}>Nexus</span>Stake
            </div>
            <span className={styles.versionBadge}>v{appVersion}</span>
          </div>
          
          <div className={styles.headerRight}>
            <div className={styles.networkInfo}>
              <span className={styles.networkStatus}></span>
              <span>{networkName}</span>
            </div>
            <div className={styles.accountBadge} onClick={disconnectWallet}>
              {accounts[0] ? `${accounts[0].slice(0, 6)}...${accounts[0].slice(-4)}` : 'Not connected'}
            </div>
          </div>
        </div>
      </header>

      <div className={styles.hero}>
        <div className={styles.container}>
          <h1 className={styles.heroTitle}>Secure Staking, <span className={styles.accentText}>Premium Returns</span></h1>
          <p className={styles.heroSubtitle}>Stake your ETH in our enterprise-grade platform and earn reliable rewards.</p>
        </div>
      </div>

      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.statsContainer}>
            <div className={styles.statCard}>
              <h3>Total Value Locked</h3>
              <p className={styles.statValue}>${tvlUsd.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
              <div className={styles.statChange}>ETH Staking Protocol</div>
            </div>
            <div className={styles.statCard}>
              <h3>Protocol</h3>
              <p className={styles.statValue}>Optimism</p>
              <div className={styles.statChange}>Layer 2 Solution</div>
            </div>
            <div className={styles.statCard}>
              <h3>Total Stakers</h3>
              <p className={styles.statValue}>{totalStakers}</p>
              <div className={styles.statChange}>Active Users</div>
            </div>
            <div className={styles.statCard}>
              <h3>Network Security</h3>
              <p className={styles.statValue}>Enterprise</p>
              <div className={styles.statChange}>Multi-sig Protection</div>
            </div>
          </div>

          <div className={styles.contentColumns}>
            <div className={styles.mainContent}>
              <div className={styles.tabsContainer}>
                <button 
                  className={`${styles.tabButton} ${activeTab === 'staking' ? styles.activeTab : ''}`}
                  onClick={() => setActiveTab('staking')}
                >
                  Staking
                </button>
                <button 
                  className={`${styles.tabButton} ${activeTab === 'referrals' ? styles.activeTab : ''}`}
                  onClick={() => setActiveTab('referrals')}
                >
                  Referrals
                </button>
              </div>
              
              {activeTab === 'staking' ? (
                <Staking 
                  web3={web3}
                  accounts={accounts}
                  stakingContract={stakingContract}
                  tokenContract={tokenContract}
                />
              ) : (
                <Referrals
                  web3={web3}
                  accounts={accounts}
                  stakingContract={stakingContract}
                />
              )}
            </div>
            <div className={styles.sideContent}>
              <div className={styles.infoBox}>
                <h3 className={styles.infoTitle}>Platform Benefits</h3>
                <ul className={styles.benefitsList}>
                  <li className={styles.benefitItem}>
                    <span className={styles.benefitIcon} role="img" aria-label="Security">🔒</span>
                    <div className={styles.benefitText}>
                      <strong>Enterprise Security</strong>
                      <p>Military-grade protection for your assets</p>
                    </div>
                  </li>
                  <li className={styles.benefitItem}>
                    <span className={styles.benefitIcon} role="img" aria-label="Money">💰</span>
                    <div className={styles.benefitText}>
                      <strong>Optimism L2 Solution</strong>
                      <p>Fast transactions with lower gas fees</p>
                    </div>
                  </li>
                  <li className={styles.benefitItem}>
                    <span className={styles.benefitIcon} role="img" aria-label="Lightning">⚡</span>
                    <div className={styles.benefitText}>
                      <strong>Efficient Protocol</strong>
                      <p>Optimized staking mechanism</p>
                    </div>
                  </li>
                  <li className={styles.benefitItem}>
                    <span className={styles.benefitIcon} role="img" aria-label="Refresh">🔄</span>
                    <div className={styles.benefitText}>
                      <strong>Reliable Platform</strong>
                      <p>Trusted by users worldwide</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerSection}>
            <h4>About NexusStake</h4>
            <p>Industry-leading staking platform built on the Ethereum blockchain. Trusted by institutional investors and individuals alike.</p>
          </div>
          <div className={styles.footerSection}>
            <h4>Resources</h4>
            <ul>
              <li><Link to="/docs">Documentation</Link></li>
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/cookies">Cookie Policy</Link></li>
            </ul>
          </div>
          <div className={styles.footerSection}>
            <h4>Connect</h4>
            <div className={styles.socialLinks}>
              <a href="https://twitter.com/nexusstake" className={styles.socialLink}>Twitter</a>
              <a href="https://discord.gg/nexusstake" className={styles.socialLink}>Discord</a>
              <a href="https://t.me/nexusstake" className={styles.socialLink}>Telegram</a>
            </div>
          </div>
        </div>
        <div className={styles.subfooter}>
          <div className={styles.copyright}>
            © 2024 NexusStake. All rights reserved.
          </div>
          <div className={styles.legalLinks}>
            <Link to="/terms">Terms</Link>
            <Link to="/cookies">Cookies</Link>
          </div>
        </div>
      </footer>

      <WalletModal isVisible={modalVisible} onClose={() => setModalVisible(false)} />
      
      {notification && (
        <Notification 
          type={notificationType} 
          message={notification} 
          onClose={closeNotification} 
        />
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <div className={styles.App}>
        <Switch>
          <Route exact path="/" component={StakingApp} />
          <Route path="/docs" component={Documentation} />
          <Route path="/terms" component={TermsOfService} />
          <Route path="/cookies" component={CookiePolicy} />
          <Route path="/faq" component={FAQ} />
        </Switch>
      </div>
    </Router>
  );
}

export default App;